import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { User } from 'oidc-client-ts';
import { BrandLogo, LoadingSpinner } from '@examflow/ui';
import { getUserManager } from '../lib/oidc';

const POST_LOGIN_REDIRECT_KEY = 'examflow_post_login_redirect';

/** Deduplicate Strict Mode double-mount so the auth code is exchanged once. */
let webSigninCallback: Promise<User> | null = null;

function signinRedirectCallbackOnce(): Promise<User> {
  if (!webSigninCallback) {
    webSigninCallback = getUserManager()
      .signinRedirectCallback()
      .finally(() => {
        queueMicrotask(() => {
          webSigninCallback = null;
        });
      });
  }
  return webSigninCallback;
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void signinRedirectCallbackOnce()
      .then(() => {
        if (!mounted) return;
        const target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) || '/';
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        const path = target.startsWith('/') ? target : `/${target}`;
        navigate(path, { replace: true });
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : '登录失败');
      });
    return () => {
      mounted = false;
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            <BrandLogo variant="mark" size={40} />
          </div>
          <h1 className="text-lg font-semibold text-slate-900">登录失败</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          <Link
            to="/login"
            className="mt-6 inline-flex rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            返回登录
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <BrandLogo variant="mark" size={40} />
        </div>
        <LoadingSpinner />
        <p className="mt-4 text-sm text-slate-500">正在完成登录…</p>
      </div>
    </div>
  );
}
